// Admin ticket inbox (portal overhaul O1): views rail, urgency-sorted list,
// detail panel with the conversation thread, and a drag-and-drop board mode.
// Mirrors `showAdminTickets` + TicketThread in the handoff prototype. O2 adds
// the working-hours target clock and thread attachments (ticketParts.tsx).
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Icon, IconButton, Input, Select } from "../../ds/DS";
import { api } from "../../lib/api";
import { useTicketDetail, type TicketDetail, type VRelease, type VTicket } from "../data";
import {
  BOARD_STATUSES,
  PRIORITIES,
  TICKET_CATEGORIES,
  fmtDateTime,
  priorityTone,
  shortAge,
  statusDot,
  statusTone,
} from "../tone";
import type { PageProps } from "../Portal";
import { ClockChip, ClockField, NewTicket, TicketThread } from "./ticketParts";
import {
  buildThread,
  firstName,
  id8,
  initials,
  postTicketMessage,
  type ThreadSend,
} from "../ticketThread";
import { TWO_PANE_QUERY, useMedia } from "../useMedia";

const WAITING = "Waiting on client";
const UNASSIGNED = "Unassigned";
const NO_RELEASE = "Not in a release";

type Layout = "list" | "board";
type ViewId =
  | "needs"
  | "due"
  | "urgent"
  | "waiting"
  | "new"
  | "mine"
  | "open"
  | "closed";

function endOfToday(): number {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

const isClosed = (t: VTicket) => t.status === "Closed";
const isWaiting = (t: VTicket) => t.status === WAITING;
// Due today: the target clock is overdue, under 25% left or falls due today
// (or the ticket's own due date does). Paused clocks don't count.
const isDue = (t: VTicket) =>
  !isClosed(t) &&
  !isWaiting(t) &&
  (t.clock.tone !== "neutral" ||
    (t.clock.dueAt != null && t.clock.dueAt <= endOfToday()) ||
    (t.dueAt != null && t.dueAt <= endOfToday()));

const PRIO_RANK: Record<string, number> = { Urgent: 0, High: 1, Normal: 2, Low: 3 };

/** Urgency rank: open before waiting before closed; then overdue, priority,
 *  needs-reply, and oldest first. */
function urgencyCompare(a: VTicket, b: VTicket): number {
  const key = (t: VTicket) => [
    isClosed(t) ? 2 : isWaiting(t) ? 1 : 0,
    isDue(t) ? 0 : 1,
    PRIO_RANK[t.priority] ?? 2,
    t.needsReply ? 0 : 1,
  ];
  const ka = key(a);
  const kb = key(b);
  for (let i = 0; i < ka.length; i++) {
    const d = (ka[i] ?? 0) - (kb[i] ?? 0);
    if (d) return d;
  }
  return isClosed(a) ? b.updatedAt - a.updatedAt : a.opened - b.opened;
}

/* ---------- views ---------- */
const VIEWS: {
  id: ViewId;
  label: string;
  icon: string;
  fn: (t: VTicket, mine: (t: VTicket) => boolean) => boolean;
}[] = [
  { id: "needs", label: "Needs reply", icon: "reply", fn: (t) => t.needsReply && !isClosed(t) },
  { id: "due", label: "Due today", icon: "clock", fn: isDue },
  {
    id: "urgent",
    label: "Urgent & high",
    icon: "zap",
    fn: (t) => !isClosed(t) && (t.priority === "Urgent" || t.priority === "High"),
  },
  { id: "waiting", label: "Waiting on client", icon: "hourglass", fn: isWaiting },
  {
    id: "new",
    label: "Not triaged",
    icon: "inbox",
    fn: (t) => t.status === "Open" && t.assignee === UNASSIGNED,
  },
  { id: "mine", label: "Assigned to me", icon: "user", fn: (t, mine) => !isClosed(t) && mine(t) },
  { id: "open", label: "All open", icon: "layers", fn: (t) => !isClosed(t) },
  { id: "closed", label: "Closed", icon: "archive", fn: isClosed },
];

/* ---------- detail panel ---------- */
function DetailPanel({
  t,
  detail,
  projectName,
  agentOptions,
  releases,
  overlay,
  onClosePanel,
  onBack,
  onStatus,
  onPriority,
  onAssignee,
  onCategory,
  onRelease,
  onOpenRelease,
  onSend,
}: {
  t: VTicket;
  detail: TicketDetail | null;
  projectName: (id: string) => string;
  agentOptions: string[];
  releases: VRelease[];
  overlay: boolean;
  onClosePanel: () => void;
  onBack?: () => void;
  onStatus: (s: string) => void;
  onPriority: (p: string) => void;
  onAssignee: (a: string) => void;
  onCategory: (c: string) => void;
  onRelease: (r: VRelease | null) => void;
  onOpenRelease: (id: string) => void;
  onSend: (m: ThreadSend) => Promise<void>;
}) {
  const closed = isClosed(t);
  const clock = t.clock;
  const recipient = t.authorSide === "client" ? firstName(t.author) : "the client";
  const fresh = detail && detail.ticket.id === t.id ? detail : null;
  const items = useMemo(() => buildThread(t, fresh), [t, fresh]);
  // The ticket's project's releases that aren't live yet, plus its current one.
  const relOpts = releases.filter(
    (r) => r.projectId === t.projectId && (r.status !== "Released" || r.id === t.releaseId),
  );
  const current = releases.find((r) => r.id === t.releaseId);
  const facts = [
    `Raised by ${t.author} via ${t.channel}`,
    t.environment,
    `Opened ${fmtDateTime(t.opened)}`,
    clock.paused ? `Clock paused while we wait on ${recipient}.` : clock.note,
  ].filter(Boolean);

  return (
    <div className={"ibx-detail" + (overlay ? " is-overlay" : "")}>
      {overlay ? <div className="ibx-strip" /> : null}
      {onBack ? (
        <button className="ibx-back" onClick={onBack}>
          <Icon name="arrow-left" size={15} />
          All tickets
        </button>
      ) : null}
      <div className="ibx-dhead">
        <div className="ibx-dhead-top">
          <span className="mono ibx-id">#{id8(t.id)}</span>
          {/* Compact category control (badge-styled select). */}
          <span className="ibx-cat" data-tone={t.category === "Issue" ? "warning" : "neutral"}>
            <select
              aria-label="Category"
              value={TICKET_CATEGORIES.includes(t.category) ? t.category : "Issue"}
              onChange={(e) => onCategory(e.target.value)}
            >
              {TICKET_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <Icon name="chevron-down" size={12} />
          </span>
          <span className="ibx-dmeta">
            {projectName(t.projectId)} · {t.author}
          </span>
          <div className="row-6" style={{ marginLeft: "auto" }}>
            <Button
              variant="secondary"
              size="sm"
              icon={closed ? "rotate-ccw" : "check"}
              onClick={() => onStatus(closed ? "Open" : "Closed")}
            >
              {closed ? "Reopen" : "Close ticket"}
            </Button>
            {overlay ? (
              <IconButton icon="x" label="Close panel" variant="ghost" onClick={onClosePanel} />
            ) : null}
          </div>
        </div>
        <h2 className="ibx-subject">{t.subject}</h2>
        <div className="ibx-seg" role="radiogroup" aria-label="Status">
          {BOARD_STATUSES.map((s) => (
            <button
              key={s}
              role="radio"
              aria-checked={s === t.status}
              className={"ibx-seg-btn" + (s === t.status ? " is-on" : "")}
              onClick={() => onStatus(s)}
            >
              <span className="ibx-dot" style={{ background: statusDot(s) }} />
              {s}
            </button>
          ))}
        </div>
        <div className="ibx-fields">
          <ClockField c={clock} />
          <div className="ibx-field">
            <div className="ibx-flabel">Priority</div>
            <Select
              size="sm"
              options={PRIORITIES}
              value={PRIORITIES.includes(t.priority) ? t.priority : "Normal"}
              onChange={(e) => onPriority(e.target.value)}
            />
          </div>
          <div className="ibx-field">
            <div className="ibx-flabel">Assigned</div>
            <Select
              size="sm"
              options={agentOptions}
              value={t.assignee}
              onChange={(e) => onAssignee(e.target.value)}
            />
          </div>
          <div className="ibx-field">
            <div className="ibx-flabel ibx-flabel-row">
              <span>Release</span>
              {current ? (
                <button className="link-btn ibx-open" onClick={() => onOpenRelease(current.id)}>
                  Open →
                </button>
              ) : null}
            </div>
            <Select
              size="sm"
              options={[NO_RELEASE, ...relOpts.map((r) => r.name)]}
              value={current?.name ?? NO_RELEASE}
              onChange={(e) => onRelease(relOpts.find((r) => r.name === e.target.value) ?? null)}
            />
          </div>
        </div>
        <div className="ibx-facts">{facts.join(" · ")}</div>
      </div>
      <TicketThread
        key={t.id}
        viewer="admin"
        items={items}
        recipient={recipient}
        onSend={onSend}
      />
    </div>
  );
}

/* ---------- inbox ---------- */
export function TicketInbox({
  tickets,
  releases,
  pid,
  scopedProjects,
  projects,
  projectName,
  me,
  reload,
  toast,
  focus,
  clearFocus,
  openItem,
}: PageProps) {
  const [layout, setLayout] = useState<Layout>("list");
  const [view, setView] = useState<ViewId>("needs");
  const [aProject, setAProject] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const [panel, setPanel] = useState(true);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  // Narrow screens show the list, then the picked ticket in its place.
  const narrow = useMedia(TWO_PANE_QUERY);

  // The server records the replier's name (else email) as the assignee.
  const myName = me.name && me.name !== "You" ? me.name : me.email;
  const mine = (t: VTicket) => t.assignee === myName;

  useEffect(() => {
    setAProject(null);
    setSel(null);
  }, [pid]);

  // Arrive on one ticket from elsewhere (e.g. a release's ticket list).
  useEffect(() => {
    if (focus?.page !== "tickets") return;
    const t = tickets.find((x) => x.id === focus.id);
    clearFocus();
    if (!t) return;
    setSel(t.id);
    setView(t.status === "Closed" ? "closed" : "open");
    setAProject(null);
    setQ("");
    setPanel(true);
  }, [focus, clearFocus, tickets]);

  const scoped = useMemo(
    () => (pid === "all" ? tickets : tickets.filter((t) => t.projectId === pid)),
    [tickets, pid],
  );
  const byId = useMemo(() => new Map(scoped.map((t) => [t.id, t])), [scoped]);
  const base = useMemo(() => {
    const n = q.trim().toLowerCase();
    return scoped
      .filter((t) => !aProject || t.projectId === aProject)
      .filter(
        (t) =>
          !n ||
          `${t.subject} ${t.id} ${t.author} ${t.authorEmail}`.toLowerCase().includes(n),
      )
      .sort(urgencyCompare);
  }, [scoped, aProject, q]);

  const vDef = VIEWS.find((v) => v.id === view) ?? (VIEWS[0] as (typeof VIEWS)[number]);
  const inView = base.filter((t) => vDef.fn(t, mine));
  const selT =
    (sel ? byId.get(sel) : undefined) ??
    (layout === "list" && !narrow ? inView[0] : undefined);
  const isBoard = layout === "board";
  const showDetail = !!selT && (!isBoard || panel) && (!narrow || panel);
  // Narrow list mode: the detail replaces the rail and list.
  const detailOnly = narrow && !isBoard && showDetail;
  const openTicket = (id: string) => {
    setSel(id);
    setPanel(true);
    if (narrow) window.scrollTo(0, 0);
  };

  const { detail, refetch } = useTicketDetail(
    selT?.projectId ?? null,
    selT?.id ?? null,
  );

  const agentOptions = useMemo(() => {
    const names = new Set<string>([myName]);
    for (const t of scoped) if (t.assignee && t.assignee !== UNASSIGNED) names.add(t.assignee);
    return [...names, UNASSIGNED];
  }, [scoped, myName]);

  const refresh = () => {
    reload();
    refetch();
  };
  const patch = (t: VTicket, body: Record<string, unknown>) =>
    api.patch(`/projects/${t.projectId}/tickets/${t.id}`, body);

  const setStatus = async (t: VTicket, status: string) => {
    if (t.status === status) return;
    setSel(t.id); // keep the worked-on ticket open even if it leaves the view
    await patch(t, { boardStatus: status });
    refresh();
    toast(`#${id8(t.id)} → ${status}`);
  };
  const setPriority = async (t: VTicket, p: string) => {
    if (t.priority === p) return;
    await patch(t, { priority: p.toLowerCase() });
    refresh();
    toast(`#${id8(t.id)} · ${p} priority`);
  };
  const setAssignee = async (t: VTicket, a: string, msg?: string) => {
    if (t.assignee === a) return;
    await patch(t, { assignee: a === UNASSIGNED ? "" : a });
    refresh();
    toast(msg ?? `#${id8(t.id)} → ${a}`);
  };
  const setCategory = async (t: VTicket, cat: string) => {
    if (t.category === cat) return;
    await patch(t, { category: cat });
    refresh();
    toast(`#${id8(t.id)} is now ${cat === "Issue" ? "an" : "a"} ${cat}`);
  };
  const setRelease = async (t: VTicket, r: VRelease | null) => {
    if ((t.releaseId ?? null) === (r?.id ?? null)) return;
    setSel(t.id);
    try {
      await patch(t, { releaseId: r?.id ?? null });
      refresh();
      toast(r ? `#${id8(t.id)} added to ${r.name}` : `#${id8(t.id)} removed from its release`);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't change the release. Try again.");
    }
  };
  const send = async (t: VTicket, m: ThreadSend) => {
    setSel(t.id);
    const r = await postTicketMessage(t, m);
    refresh();
    const who = t.authorSide === "client" ? firstName(t.author) : "the client";
    if (m.note) toast("Note saved.");
    else if (m.wait) toast(`Reply sent to ${who}. Waiting on them.`);
    else {
      toast(`Reply sent to ${who}.`);
      if (r.boardStatus) toast(`#${id8(t.id)} → ${r.boardStatus}`);
    }
  };

  // Keyboard: j/k move, a assigns to me, b toggles layout, Esc closes the
  // board panel. Ignored while typing or with a dialog open. Read through a
  // ref so the listener always sees the latest state.
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyRef.current = (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const el = document.activeElement as HTMLElement | null;
    if (
      el &&
      (el.tagName === "INPUT" ||
        el.tagName === "TEXTAREA" ||
        el.tagName === "SELECT" ||
        el.isContentEditable)
    )
      return;
    if (composing || document.querySelector(".ds-dialog")) return;
    if (e.key === "Escape") {
      if (isBoard || narrow) setPanel(false);
      return;
    }
    // j/k/a/b assume the three-pane layout; off on narrow (touch) screens.
    if (narrow) return;
    if (e.key === "j" || e.key === "k") {
      const refs = inView.map((t) => t.id);
      if (!refs.length) return;
      const i = selT ? refs.indexOf(selT.id) : -1;
      const n = e.key === "j" ? Math.min(refs.length - 1, i + 1) : Math.max(0, i - 1);
      setSel(refs[n] ?? null);
      setPanel(true);
    } else if (e.key === "b") {
      setLayout((l) => (l === "list" ? "board" : "list"));
    } else if (e.key === "a" && selT) {
      if (mine(selT)) toast("Already assigned to you.");
      else void setAssignee(selT, myName, "Assigned to you");
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const drop = (status: string) => {
    const t = dragId ? byId.get(dragId) : undefined;
    setDragId(null);
    setOverCol(null);
    if (t) void setStatus(t, status);
  };

  const openCount = (projectId: string) =>
    scoped.filter((t) => t.projectId === projectId && !isClosed(t)).length;

  return (
    <div className={"ibx" + (detailOnly ? " is-detail" : "")}>
      <div className="ibx-toolbar">
        <div className="ibx-search">
          <Input
            icon="search"
            size="sm"
            placeholder="Search subject, ID or person"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="ibx-shortcuts">
          {isBoard
            ? "Drag cards between columns · b switches layout"
            : "j / k to move · a assigns to you · b for board"}
        </span>
        <span style={{ flex: 1 }} />
        <div className="ibx-switch" role="radiogroup" aria-label="Layout">
          {(
            [
              ["list", "List", "list"],
              ["board", "Board", "kanban"],
            ] as const
          ).map(([id, label, icon]) => (
            <button
              key={id}
              role="radio"
              aria-checked={layout === id}
              className={"ibx-switch-btn" + (layout === id ? " is-on" : "")}
              onClick={() => {
                setLayout(id);
                if (id === "list") setPanel(true);
              }}
            >
              <Icon name={icon} size={14} />
              {label}
            </button>
          ))}
        </div>
        <Button size="sm" icon="plus" onClick={() => setComposing(true)}>
          Log a ticket
        </Button>
      </div>

      <div
        className={
          "ibx-body" + (isBoard ? " is-board" : "") + (detailOnly ? " is-detail" : "")
        }
      >
        {detailOnly ? null : (
          <div className="ibx-rail">
            <div className="ibx-rail-sec">Views</div>
            {VIEWS.map((v) => (
              <button
                key={v.id}
                className={"ibx-view" + (v.id === view ? " is-on" : "")}
                onClick={() => setView(v.id)}
              >
                <Icon name={v.icon} size={15} />
                <span>{v.label}</span>
                <span className="ibx-view-n mono">{base.filter((t) => v.fn(t, mine)).length}</span>
              </button>
            ))}
            <div className="ibx-rail-sec" style={{ paddingTop: 22 }}>
              Projects
            </div>
            {scopedProjects.map((p) => (
              <button
                key={p.id}
                className={"ibx-view ibx-proj" + (aProject === p.id ? " is-on" : "")}
                onClick={() => setAProject(aProject === p.id ? null : p.id)}
              >
                <span className="ibx-proj-code mono">{p.code}</span>
                <span className="ibx-proj-name">{p.name}</span>
                <span className="ibx-view-n mono">{openCount(p.id)}</span>
              </button>
            ))}
          </div>
        )}

        {detailOnly ? null : !isBoard ? (
          <div className="ibx-list">
            <div className="ibx-list-head">
              <span className="ibx-list-title">
                {vDef.label} <span className="mono">{inView.length}</span>
              </span>
              <span className="ibx-list-sort">Most urgent first</span>
            </div>
            <div className="ibx-list-scroll">
              {inView.map((t) => {
                const on = !!selT && selT.id === t.id;
                return (
                  <button
                    key={t.id}
                    className={"ibx-row" + (on ? " is-on" : "") + (t.needsReply ? " is-unread" : "")}
                    onClick={() => openTicket(t.id)}
                  >
                    <span className="ibx-unread" />
                    <span className="ibx-row-main">
                      <span className="ibx-row-subject">{t.subject}</span>
                      <span className="ibx-row-meta mono">
                        #{id8(t.id)} · {projectName(t.projectId)} · {t.author}
                      </span>
                      <span className="row-6" style={{ marginTop: 2 }}>
                        <Badge tone={statusTone(t.status)} dot>
                          {t.status}
                        </Badge>
                        {t.priority === "Urgent" || t.priority === "High" ? (
                          <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>
                        ) : null}
                      </span>
                    </span>
                    <span className="ibx-row-side">
                      <span className="ibx-age mono">{shortAge(t.updatedAt)}</span>
                      <ClockChip c={t.clock} />
                    </span>
                  </button>
                );
              })}
              {inView.length === 0 ? (
                <div className="ibx-empty">
                  <div className="ibx-empty-title">Nothing in this view</div>
                  <div>That queue is clear. Pick another view or clear the search.</div>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="ibx-board">
            {BOARD_STATUSES.map((s) => {
              const col = inView.filter((t) => t.status === s);
              return (
                <div
                  key={s}
                  className={"ibx-col" + (overCol === s ? " is-over" : "")}
                  onDragOver={(e) => {
                    if (!dragId) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (overCol !== s) setOverCol(s);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                      setOverCol((c) => (c === s ? null : c));
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    drop(s);
                  }}
                >
                  <div className="ibx-col-head">
                    <span className="ibx-col-name">
                      <span className="ibx-dot" style={{ background: statusDot(s) }} />
                      {s}
                    </span>
                    <span className="mono ibx-col-n">{col.length}</span>
                  </div>
                  {col.map((t) => {
                    const on = showDetail && selT?.id === t.id;
                    const assigned = t.assignee !== UNASSIGNED;
                    return (
                      <div
                        key={t.id}
                        className={
                          "ibx-card" + (on ? " is-on" : "") + (dragId === t.id ? " is-dragging" : "")
                        }
                        draggable
                        role="button"
                        tabIndex={0}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", t.id);
                          setDragId(t.id);
                        }}
                        onDragEnd={() => {
                          setDragId(null);
                          setOverCol(null);
                        }}
                        onClick={() => openTicket(t.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openTicket(t.id);
                          }
                        }}
                      >
                        <div className="ibx-card-top">
                          <span className="mono ibx-id">#{id8(t.id)}</span>
                          <ClockChip c={t.clock} />
                        </div>
                        <div className="ibx-card-subject">{t.subject}</div>
                        {t.priority === "Urgent" || t.priority === "High" ? (
                          <div>
                            <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>
                          </div>
                        ) : null}
                        {t.needsReply ? (
                          <div className="ibx-card-reply">
                            <Icon name="reply" size={13} />
                            {firstName(t.author)} replied · {shortAge(t.updatedAt)}
                          </div>
                        ) : null}
                        <div className="ibx-card-foot">
                          <span className="mono">{projectName(t.projectId)}</span>
                          <span
                            className={"ibx-as mono" + (assigned ? " is-set" : "")}
                            title={assigned ? t.assignee : UNASSIGNED}
                          >
                            {assigned ? initials(t.assignee) : "–"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {col.length === 0 ? <div className="ibx-col-empty">Drag a ticket here</div> : null}
                </div>
              );
            })}
          </div>
        )}

        {showDetail && selT ? (
          <DetailPanel
            t={selT}
            detail={detail}
            projectName={projectName}
            agentOptions={agentOptions}
            releases={releases}
            overlay={isBoard}
            onClosePanel={() => setPanel(false)}
            onBack={detailOnly ? () => setPanel(false) : undefined}
            onStatus={(s) => void setStatus(selT, s)}
            onPriority={(p) => void setPriority(selT, p)}
            onAssignee={(a) => void setAssignee(selT, a)}
            onCategory={(c) => void setCategory(selT, c)}
            onRelease={(r) => void setRelease(selT, r)}
            onOpenRelease={(id) => openItem("releases", id)}
            onSend={(m) => send(selT, m)}
          />
        ) : !isBoard && !narrow ? (
          <div className="ibx-detail-empty">
            <Icon name="message-square" size={22} />
            <span>Pick a ticket to see the conversation.</span>
          </div>
        ) : null}
      </div>

      <NewTicket
        open={composing}
        isAdmin
        projects={projects}
        defaultPid={aProject ?? pid}
        onClose={() => setComposing(false)}
        onCreated={(id) => {
          reload();
          setSel(id);
          setView("open");
          setAProject(null);
          setQ("");
          setPanel(true);
          toast(`Ticket #${id8(id)} logged.`);
        }}
      />
    </div>
  );
}
